'use client';
import { memo, useEffect, useMemo } from 'react';
import { VStack, Menu, MenuButton, Button, MenuList, MenuItem } from '@chakra-ui/react';

import LabelText from 'shared/InputsLabelText';
import ErrorText from 'shared/InputsErrorText';
import NextImage from 'shared/NextImage';
import InterText from 'shared/InterText';

import DownAngle from 'assets/icons/downAngle.svg';
import { DEFAULT_TRANSITION } from 'constants/global';
import { scrollBarStyle } from 'constants/scrollBarStyle';
import { inputsWrapperStyle } from 'constants/inputsWrappersStyles';

const SelectField = ({
  label = '',
  hint = '',
  error = false,
  onChange,
  name,
  trigger,
  value,
  options = [],
  placeholder,
}) => {
  const showValue = useMemo(
    () => options?.find(data => data?.value === value)?.mask || value,
    [options, value],
  );

  useEffect(() => {
    return () => trigger && trigger(name);
  }, [name, trigger]);

  return (
    <VStack w="full" align="start" gap="0" pos="relative">
      <LabelText value={value} label={label} />

      <Menu flip={false} preventOverflow={false} isLazy matchWidth gutter={2}>
        <MenuButton
          as={Button}
          role="group"
          sx={inputsWrapperStyle('select', error)}
          rightIcon={
            <NextImage
              src={DownAngle}
              alt="Иконка стрелочки"
              w="18px"
              h="18px"
              transition={DEFAULT_TRANSITION}
              _groupActive={{ transform: 'rotate(-180deg)' }}
            />
          }
        >
          <InterText
            textStyle="b4"
            maxW={{ mobile: '250px', tablet: 'unset' }}
            fontSize={
              value
                ? { mobile: '16px', laptop: '18px' }
                : { mobile: '13px', mg: '16px', laptop: '18px' }
            }
            fontWeight="400"
            display="block"
            overflow="hidden"
            textOverflow="ellipsis"
            textAlign="start"
            color={value ? 'ourBlack' : 'ourGrayTwo'}
          >
            {value ? showValue : placeholder}
          </InterText>
        </MenuButton>

        <MenuList
          data-lenis-prevent
          overflow="hidden"
          role="group"
          zIndex="9999"
          cursor="pointer"
          alignItems="start"
          bgColor="ourWhite"
          borderRadius="18px"
          borderColor="ourGrayFour"
          p="0"
          maxH={{ mobile: '340px', laptop: '420px' }}
          overflowY="auto"
          overscrollBehavior="contain"
          sx={scrollBarStyle}
        >
          {options?.map((option, i) => (
            <MenuItem
              key={i}
              isFocusable={false}
              overflow="hidden"
              bgColor="ourWhite"
              textStyle="b4"
              p={{ mobile: '9px 20px', tablet: '7px 24px' }}
              transition={DEFAULT_TRANSITION}
              _hover={{ bgColor: '#FFF6DB' }}
              onClick={() => {
                onChange(option?.value ?? option);
                if (trigger) trigger(name);
              }}
            >
              <InterText textStyle="b4" fontWeight="400" color="ourBlack">
                {option?.mask ?? option}
              </InterText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <ErrorText error={error} hint={hint} />
    </VStack>
  );
};

export default memo(SelectField);
